import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarLightReachReaders } from "./solar-price-dependents"
import type {
  SolarProposalConsumptionDto,
  UpdateSolarProposalConsumptionDto,
} from "../dto/solar-proposal-consumption.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type UpdateSolarProposalConsumptionVariables = {
  proposalId: string
  body: UpdateSolarProposalConsumptionDto
}

/**
 * Saves manual usage. The answer is the consumption read itself, so it
 * replaces that cache entry. Nothing recalculates: the header and design
 * reads are refetched for the new offset, and the savings, utility and
 * contract readiness reads so they report out of date.
 */
export default function useUpdateSolarProposalConsumption(): UseMutationResult<
  SolarProposalConsumptionDto,
  unknown,
  UpdateSolarProposalConsumptionVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      proposalId,
      body,
    }: UpdateSolarProposalConsumptionVariables) =>
      solarProposalService.putConsumption(proposalId, body),
    onSuccess: (consumption, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.consumption(proposalId),
        consumption,
      )
      // Not awaited: the save is done, and the step shouldn't read "Saving…"
      // while the other sections refetch.
      for (const queryKey of [
        queryKeys.solarProposal.byId(proposalId),
        queryKeys.solarProposal.design(proposalId),
        queryKeys.solarProposal.savings(proposalId),
        queryKeys.solarProposal.share(proposalId),
        queryKeys.solarProposal.utility(proposalId),
        queryKeys.solarProposal.contract(proposalId),
      ]) {
        void queryClient.invalidateQueries({ queryKey, exact: true })
      }
      // Savings out of date hold Get LightReach pricing.
      invalidateSolarLightReachReaders(queryClient, proposalId)
    },
    // A lock that appeared changes what the form may do; the draft is local.
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.consumption(proposalId),
        exact: true,
      })
    },
  })
}
